import { UserAccessManager } from './user-access-manager';
import { DomainId, SystemRoleId, CommonRoleId } from './claim-id';
import {
  LoginSecurityContext,
  LoginSecurityRoleGrant,
} from '../api/agwa-api-model';

/**
 * Merges one or more claims objects into a single claims object.
 * Later claims override earlier ones for matching keys (case-insensitive).
 * @param claimsList Array of claims objects to merge
 * @returns A new merged claims object
 */
export function mergeClaims(
  ...claimsList: Readonly<Record<string, unknown>>[]
): Record<string, string> {
  const merged: Record<string, string> = {};
  for (const claims of claimsList) {
    for (const [key, value] of Object.entries(claims)) {
      merged[key.toLowerCase()] = String(value);
    }
  }
  return merged;
}
export function getUserAccessManager(
  ...claims: Readonly<Record<string, unknown>>[]
): UserAccessManager {
  return new UserAccessManagerImpl(mergeClaims(...claims));
}
/**
 * Implementation of the UserAccessManager interface
 * Provides methods to manage user access claims, roles, features, and permissions
 */
export class UserAccessManagerImpl implements UserAccessManager {
  private claims: Record<string, string>;
  private securityContext?: LoginSecurityContext;
  private roleCodeIndex: Record<string, LoginSecurityRoleGrant> = {};
  private moduleRoleIndex: Record<string, LoginSecurityRoleGrant[]> = {};
  private _loginGroupId?: string;
  private _loginGroupName?: string;

  /**
   * Creates a new instance of UserAccessManagerImpl
   * @param claims Initial claims dictionary
   * @param securityContext Security context with roles and domains
   * @param loginGroupId Direct group ID from credentials
   * @param loginGroupName Direct group name from credentials
   */
  constructor(
    claims: Readonly<Record<string, unknown>> = {},
    securityContext?: LoginSecurityContext,
    loginGroupId?: string,
    loginGroupName?: string
  ) {
    // Convert all keys to lowercase for case-insensitive operations
    // and convert values to strings for consistent handling
    this.claims = Object.entries(claims).reduce(
      (acc, [key, value]) => {
        acc[key.toLowerCase()] = String(value);
        return acc;
      },
      {} as Record<string, string>
    );
    this.securityContext = securityContext;
    this._loginGroupId = loginGroupId;
    this._loginGroupName = loginGroupName;
    this.rebuildRoleIndexes();
  }

  private rebuildRoleIndexes() {
    this.roleCodeIndex = {};
    this.moduleRoleIndex = {};
    const roles = this.securityContext?.roles ?? [];
    for (const role of roles) {
      const normalized = this.normalizeRoleCode(role.code);
      this.roleCodeIndex[normalized] = role;
      const moduleCode = (role.moduleCode || '__unscoped__').toLowerCase();
      if (!this.moduleRoleIndex[moduleCode]) {
        this.moduleRoleIndex[moduleCode] = [];
      }
      this.moduleRoleIndex[moduleCode].push(role);
    }
  }

  private normalizeRoleCode(roleName: string): string {
    return roleName.replace(/^role\./i, '').toLowerCase();
  }

  /**
   * Gets the current claims dictionary (only those starting with "claim.")
   * @returns The current claims with "claim." prefix
   */
  public getClaims(): Record<string, string> {
    const filtered: Record<string, string> = {};
    for (const [key, value] of Object.entries(this.claims)) {
      if (key.startsWith('claim.')) {
        filtered[key] = value;
      }
    }
    return filtered;
  }

  public getRoles(): Record<string, string> {
    const filtered: Record<string, string> = {};
    const contextRoles = this.securityContext?.roles ?? [];
    for (const role of contextRoles) {
      filtered[`role.${role.code}`] = 'true';
    }
    for (const [key, value] of Object.entries(this.claims)) {
      if (key.startsWith('role.')) {
        filtered[key] = value;
      }
    }
    return filtered;
  }
  public getFeatures(): Record<string, string> {
    const filtered: Record<string, string> = {};
    for (const [key, value] of Object.entries(this.claims)) {
      if (key.startsWith('feature.')) {
        filtered[key] = value;
      }
    }
    return filtered;
  }
  public getRolesList(): string[] {
    const contextRoles = (this.securityContext?.roles ?? []).map(
      (role) => `role.${role.code}`
    );
    const claimRoles = Object.entries(this.claims)
      .filter(
        ([key, value]) =>
          key.startsWith('role.') && value.toLowerCase() === 'true'
      )
      .map(([key]) => key);

    const combined = [...contextRoles, ...claimRoles];
    return Array.from(new Set(combined));
  }

  public getFeaturesList(): string[] {
    return Object.entries(this.claims)
      .filter(
        ([key, value]) =>
          key.startsWith('feature.') && value.toLowerCase() === 'true'
      )
      .map(([key]) => key);
  }

  public getPermissionsList(): string[] {
    return Object.entries(this.claims)
      .filter(
        ([key, value]) =>
          key.startsWith('permission.') && value.toLowerCase() === 'true'
      )
      .map(([key]) => key);
  }

  claimAsGuid(claimName: string): string | null {
    const value = this.keyValue(`claim.${claimName}`);
    if (!value) return null;
    return value;
  }

  claimAsInt(claimName: string): number | null {
    const value = this.keyValue(`claim.${claimName}`);
    if (!value) return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  }

  keyExists(keyName: string): boolean {
    return keyName.toLowerCase() in this.claims;
  }

  removeKey(keyName: string): UserAccessManager {
    const lowerKey = keyName.toLowerCase();
    if (lowerKey in this.claims) {
      delete this.claims[lowerKey];
    }
    return this;
  }

  keyValue(keyName: string): string | null {
    return this.claims[keyName.toLowerCase()] ?? null;
  }
  claimValue(keyName: string): string | null {
    return this.keyValue(`claim.${keyName}`) ?? null;
  }

  isTrue(keyName: string): boolean {
    const value = this.keyValue(keyName);
    if (value === undefined || value === null) return false;
    // Handle both string "true" and boolean true
    if (typeof value === 'boolean') return value;
    return String(value).toLowerCase() === 'true';
  }

  hasRole(roleName: string): boolean {
    if (this.isAdminRolePresent()) return true;
    return this.hasRoleInContext(roleName) || this.isTrue(`role.${roleName}`);
  }

  hasFeature(featureName: string): boolean {
    if (this.isAdminRolePresent()) return true;
    return this.isTrue(`feature.${featureName}`);
  }
  hasClaim(claimName: string): boolean {
    if (this.isAdminRolePresent()) return true;
    return this.isTrue(`claim.${claimName}`);
  }
  hasPermission(permissionName: string): boolean {
    if (this.isAdminRolePresent()) return true;
    return this.isTrue(`permission.${permissionName}`);
  }

  getSecurityContext() {
    return this.securityContext;
  }

  getRoleAssignments() {
    return this.securityContext?.roles ?? [];
  }

  getRoleSets() {
    return this.securityContext?.roleSets ?? [];
  }

  getDomainGrants() {
    return this.securityContext?.domains ?? [];
  }

  getSiteDomainGrants() {
    return this.securityContext?.siteDomains ?? [];
  }

  /**
   * Checks if the user's group has access to a specific domain
   * Checks both group-level domains and site-level domains
   * @param domainCode The domain code to check (e.g., 'AGWA.SystemTenant')
   */
  hasDomain(domainCode: string): boolean {
    if (this.isAdminRolePresent()) return true;
    const normalized = domainCode.toLowerCase();

    // Check group-level domains
    const domains = this.securityContext?.domains ?? [];
    const hasGroupDomain = domains.some(
      (d) => d.domainCode.toLowerCase() === normalized && d.active
    );
    if (hasGroupDomain) return true;

    // Check site-level domains
    const siteDomains = this.securityContext?.siteDomains ?? [];
    const hasSiteDomain = siteDomains.some(
      (d) => d.domainCode.toLowerCase() === normalized && d.active
    );
    return hasSiteDomain;
  }

  getModuleRoleIndex() {
    const copy: Record<string, LoginSecurityRoleGrant[]> = {};
    for (const [key, value] of Object.entries(this.moduleRoleIndex)) {
      copy[key] = [...value];
    }
    return copy;
  }

  private hasRoleInContext(roleName: string): boolean {
    const normalized = this.normalizeRoleCode(roleName);
    return Boolean(this.roleCodeIndex[normalized]);
  }

  private isAdminRolePresent(): boolean {
    return (
      this.hasRoleInContext(CommonRoleId.admin) ||
      this.isTrue(`role.${CommonRoleId.admin}`)
    );
  }

  get groupId(): string | null {
    // Use concrete property from credentials (no fallback to claims as they're deprecated)
    return this._loginGroupId || null;
  }
  get groupName(): string | null {
    // Use concrete property from credentials (no fallback to claims as they're deprecated)
    return this._loginGroupName || null;
  }
  get isAdmin(): boolean {
    return this.isAdminRolePresent();
  }
  get isSystemTenant(): boolean {
    // SystemTenant is a domain, not a role
    return this.hasDomain(DomainId.systemTenant);
  }
  get isGroupAdmin(): boolean {
    return this.hasRole(SystemRoleId.memberAdmin);
  }
  get isUserAdmin(): boolean {
    return this.hasRole(SystemRoleId.userAdmin);
  }
  get isStaffAdmin(): boolean {
    return this.hasRole(SystemRoleId.staffAdmin);
  }
  get mayReadRoster(): boolean {
    // The role itself, never through the admin bypass hasRole applies: the server checks the
    // role code alone (LoginSecurityContextExtensions.HasAnyRole), and admin implies nothing there.
    return this.hasRoleInContext(SystemRoleId.userAdmin);
  }
}
